import React, { useState, useCallback, useEffect } from 'react';
import { Stack, CircularProgress, Box } from '@mui/material';

import { useAspenSocket } from 'hooks/useAspenSocket';
import { Activity } from 'types/aspen/activity';
import { GetAssetsPayload, GetAssetsRxMsg } from 'types/aspen/socketMessages';

import styles from './activityImage.module.scss';

interface ActivityImageProps {
  activity: Activity;
  alt?: string;
}

export const ActivityImage = ({ activity, alt }: ActivityImageProps) => {
  const [imgLoaded, setImgLoaded] = useState<boolean>(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>(activity.imageUrl);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [hasAttemptedRefresh, setHasAttemptedRefresh] = useState<boolean>(false);

  const { sendRequest } = useAspenSocket();
  const { rotation = 0, resolution } = activity;
  const imageAspectRatio = resolution ? resolution[0] / resolution[1] : undefined;

  // Reset state when activity changes (new event selected)
  useEffect(() => {
    setCurrentImageUrl(activity.imageUrl);
    setImgLoaded(false);
    setRefreshing(false);
    setHasAttemptedRefresh(false);
  }, [activity.imageUrl, activity.imageMeta?.assetId]);

  const refreshSignedUrl = useCallback(async () => {
    if (refreshing || hasAttemptedRefresh || !activity.imageMeta?.assetId) {
      return;
    }

    setRefreshing(true);
    setHasAttemptedRefresh(true);

    try {
      // Use generic sendRequest method with proper types
      type AssetsResponse = GetAssetsRxMsg['payload'];
      type AssetsPayload = GetAssetsPayload;

      const { response } = await sendRequest<AssetsResponse, AssetsPayload>(
        'get_assets',
        'assets',
        { assetIds: [activity.imageMeta.assetId] },
        { timeout: 10000 },
      );

      const asset = response.assets[0];
      if (asset.status === 200 && asset.asset?.signedUrl) {
        setCurrentImageUrl(asset.asset.signedUrl);
        // Reset loaded state to trigger new load attempt
        setImgLoaded(false);
      }
    } catch {
      // Silently fail - image will remain hidden (empty placeholder)
    } finally {
      setRefreshing(false);
    }
  }, [activity.imageMeta?.assetId, sendRequest, refreshing, hasAttemptedRefresh]);

  const handleImageError = useCallback(() => {
    // Only attempt refresh once if we have an assetId
    if (activity.imageMeta?.assetId && !hasAttemptedRefresh && !refreshing) {
      refreshSignedUrl();
    }
    // If no assetId or already attempted refresh, silently fail (empty placeholder)
  }, [refreshSignedUrl, activity.imageMeta?.assetId, hasAttemptedRefresh, refreshing]);

  const handleImageLoad = useCallback(() => {
    setImgLoaded(true);
  }, []);

  return (
    <Stack className={styles.imageContainer} sx={{ position: 'relative' }}>
      <img
        loading="lazy"
        className={imgLoaded ? styles.imageVisible : styles.imageHidden}
        data-rotation={rotation}
        src={currentImageUrl}
        alt={alt}
        style={imageAspectRatio && resolution ? { aspectRatio: `${resolution[0]} / ${resolution[1]}` } : undefined}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />

      {refreshing && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: 1,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={20} sx={{ color: 'white' }} />
        </Box>
      )}
    </Stack>
  );
};
